import { gql } from "@apollo/client"
import { useCurrencySymbol } from "hooks"
import type { SymbolType } from "lib/apollo/types"
import { useEffect, useState } from "react"
import type { CampaignThresholdType } from "@shared/graphql.types"
import { getClient } from "lib/apolloClient"
import { withGlobalSpinner } from "lib/api"
import { datadogLogs } from "@datadog/browser-logs"

export const GET_CAMPAIGN_THRESHOLDS = gql`
  query getCampaignThresholds {
    getCampaignThresholds {
      threshold
      campaign
    }
  }
`

export type CampaignThresholdsResponse = {
  getCampaignThresholds: CampaignThresholdType[]
}

const getFreeShippingThreshold = async (): Promise<CampaignThresholdType[]> => {
  const client = getClient()

  const response = await client.query<CampaignThresholdsResponse>({
    query: GET_CAMPAIGN_THRESHOLDS,
    fetchPolicy: "cache-first",
    context: {
      sessionStrategy: "REDIRECT_TO_LOGIN",
    },
  })

  return response.data.getCampaignThresholds
}

const thresholdKeyMapper = {
  STANDARD_SHIPPING: "shippingThreshold",
  SAMPLES: "samplesThreshold",
  ANNIVERSARY_GIFT: "anniversaryGiftThreshold",
  WELCOME_GIFT: "welcomeGiftThreshold",
} as const

type CampaignThresholdsKeys =
  (typeof thresholdKeyMapper)[keyof typeof thresholdKeyMapper]
type CampaignThresholds = Record<CampaignThresholdsKeys, number | null>

const defaultThresholds = Object.fromEntries<null | number>(
  Object.values(thresholdKeyMapper).map((key) => [key, null])
) as CampaignThresholds

export type UseCampaignThresholds = () => CampaignThresholds & {
  currencySymbol: SymbolType
}

export const useCampaignThresholds: UseCampaignThresholds = () => {
  const [allThresholds, setAllThresholds] = useState({ ...defaultThresholds })
  const currencySymbol = useCurrencySymbol()

  useEffect(() => {
    withGlobalSpinner(getFreeShippingThreshold)()
      .then((campaignThresholds) => {
        const thresholds = campaignThresholds.reduce(
          (acc, item) => {
            const thresholdKey = thresholdKeyMapper[item.campaign]
            if (thresholdKey) {
              acc[thresholdKey] = item.threshold ?? null
            }
            return acc
          },
          { ...defaultThresholds }
        )
        if (!thresholds.shippingThreshold) {
          throw new Error("Shipping threshold not found")
        }
        setAllThresholds(thresholds)
      })
      .catch((error) => {
        datadogLogs.logger.error(
          "Failed fetching free shipping threshold",
          error as Error
        )
      })
  }, [])

  return {
    ...allThresholds,
    currencySymbol,
  }
}
